import { useState } from 'react';
import { Variant } from 'shared/types/product';
import { Section as SpecificationSection } from 'shared/types/query/PDPSpecificationConfiguration';
import ForwardIcon from '../../../../../components/icons/forward';
import WarningIcon from '../../../../../components/icons/warning-icon';
import { transformAttributeForDisplay } from '../../../../../helpers/utils/product/transformAttributeForDisplay';

interface ProductSpecificationProps {
  specificationSections: SpecificationSection[];
  selectedVariant?: Variant;
  shouldShowProp65Warning: boolean;
}

const ProductSpecification = ({
  specificationSections,
  selectedVariant,
  shouldShowProp65Warning,
}: ProductSpecificationProps) => {
  const [accordionIsOpen, setAccordionIsOpen] = useState(false);

  const isSectionEmpty = (section: SpecificationSection) => {
    return section.attributes.filter((attr) => !!selectedVariant?.attributes?.[attr.key]).length === 0;
  };
  return (
    <div className="mt-8 max-w-none" data-testid="accordion-product-specifications">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {specificationSections
          .filter((section) => !isSectionEmpty(section))
          .map((section, index) => (
            <div key={index}>
              <h2 className="mb-6 ml-4 text-xl font-semibold text-primary-gold">{section.title}</h2>
              <ul>
                {section.attributes
                  .filter((attr) => !!selectedVariant?.attributes?.[attr.key])
                  .map((attr, i) => (
                    <li key={i} className="flex gap-2 p-4 text-primary-black odd:bg-primary-cream">
                      <p className="font-bold">{attr.label}:</p>
                      <p>{transformAttributeForDisplay(selectedVariant?.attributes?.[attr.key])}</p>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
      </div>
      {shouldShowProp65Warning && (
        <section className="mt-7">
          <button type="button">
            <strong
              className="flex min-w-[83px] gap-3 text-sm font-semibold leading-5"
              onClick={() => setAccordionIsOpen(!accordionIsOpen)}
            >
              <p>California Residents See PROP 65 WARNINGS</p>
              <section className={accordionIsOpen ? 'ml-2 -rotate-90' : 'rotate-90'}>
                <ForwardIcon />
              </section>
            </strong>
          </button>
          {accordionIsOpen && (
            <section className="text-sm font-normal leading-6">
              <p className="mt-3 text-sm font-normal leading-5">
                <span className="uppercase">
                  <WarningIcon className="inline h-[18px] w-5 fill-[#d5a806]" />
                  Warning
                </span>
                <span>&nbsp;{selectedVariant?.attributes?.prop65CaResidents}&nbsp;</span>
                <span>For more information, go to www.P65Warnings.ca.gov</span>
              </p>
              <p className="mt-5">
                The warning listed on our website is in accordance with a law implemented by the state of California
                called Proposition-65. Under this regulation, California law requires businesses to provide warnings for
                products containing chemicals that the state of California has identified as carcinogens or reproductive
                toxins. Recent amendments to this law require any retailers selling to California residents to list this
                warning on their website.
              </p>
              <p className="mt-5">
                There are currently almost 1,000 substances on the Proposition-65 list, ranging from cigarette smoke and
                asbestos to aspirin and wood dust. One of the materials listed is lead, and because most light fixtures
                contain brass in the sockets or in the frames of the shades, we are required to put this warning label
                on our light fixtures. Brass is a metal alloy consisting of copper, zinc and small amounts of lead,
                which is what triggers the “lead” warning under California Proposition-65 since lead has been shown to
                cause cancer when it is ingested.
              </p>
              <p className="mt-5">
                We hope that this explains things a little more clearly. If you have any additional questions, we would
                suggest visiting the website mentioned above for more information and the complete list of substances
                that require warnings under Proposition-65.
              </p>
            </section>
          )}
        </section>
      )}
    </div>
  );
};

export default ProductSpecification;
